import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { InputFieldAutocompleteComponent, IOption, UiField } from '@mng/mng-inputs';
import { getPartnerInfoAsString, IPartnerInfo } from '@mng/mgs-lib';
import { SubscriptSizing } from '@angular/material/form-field';
import { PartnerService } from '../partner.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PartnerAuswahlService } from './partner-auswahl.service';
import { AuthStateFacade } from '../../auth/auth-state.facade';
import { DEFAULT_ACCENT_COLOR } from '@mng/mng-ts-helpers';

@Component({
  selector: 'mng-mgs-partner-auswahl',
  imports: [InputFieldAutocompleteComponent],
  template: `
    <mng-inputfield-autocomplete
      [ngStyle]="ngStyle()"
      [field]="field()"
      (selectedEvent)="onPartnerSelected($event)"
      [subscriptSizing]="subscriptSizing()"
      panelWidth="600px"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MngMgsPartnerAuswahlComponent implements OnInit {
  readonly #domSanitizer = inject(DomSanitizer);
  readonly #authStateFacade = inject(AuthStateFacade);

  readonly #partnerAuswahlService = inject(PartnerAuswahlService);

  ohneInaktive = input(false);

  #partnerList = computed(() =>
    this.#authStateFacade.isAbrechnungUser()
      ? this.partnerService.hotelAbrechnungPartnerList()
      : this.ohneInaktive()
        ? this.partnerService.partnerListSortiertNachTypOhneInaktive()
        : this.partnerService.partnerListSortiertNachTyp()
  );

  partnerSelection = output<IPartnerInfo | undefined>();
  subscriptSizing = input<SubscriptSizing>('dynamic');

  updateRoute = input(true);

  field = signal<UiField>(undefined);

  ngStyle = input({});

  constructor(private partnerService: PartnerService) {
    if (partnerService.partnerList()?.length < 1) {
      console.error('PartnerService - Partner müssen bereits geladen sein (z.B. über partnerResolver');
    }
  }

  ngOnInit(): void {
    const selectedPartnerId = this.#partnerAuswahlService.selectedPartnerId();

    const optionHtmlInfo = (partner: IPartnerInfo) => {
      const inaktivHtml = partner.inaktiv
        ? ' <div style="color: darkred; padding-right: 8px;"><strong>Inaktiv</strong></div> '
        : '';
      const betriebstellenkeyOut = partner.betriebstellenkey
        ? `<div style='width: 70px'>${partner.betriebstellenkey}</div>`
        : '';
      const detailHtml = `<div style='display: flex; gap: 6px; font-size: 10px;'>${inaktivHtml} ${betriebstellenkeyOut} <div style='width: 50px;'>ID: <strong>${partner.id}</strong></div></div>`;
      const partnerNameHtml = `<div style='color: ${DEFAULT_ACCENT_COLOR}'>${partner.name}</div>`;
      const outHtml = `<div style='width: 100%; font-size: small;display: flex;  align-items: center;  gap: 2px;  justify-content: space-between;'>${partnerNameHtml}${detailHtml}</div>`;

      return this.#domSanitizer.bypassSecurityTrustHtml(outHtml);
    };

    const options = this.#partnerList().map(
      (partner) =>
        ({ name: getPartnerInfoAsString(partner, true), htmlInfo: optionHtmlInfo(partner), value: partner }) as IOption
    );

    const selectedPartner = selectedPartnerId
      ? this.#partnerList().find((partner) => partner.id === selectedPartnerId)
      : undefined;
    this.field.set(new UiField('partner', selectedPartner, 'Partnerauswahl').withOptions(options));
  }

  onPartnerSelected(partnerInfo: IPartnerInfo | undefined) {
    this.partnerSelection.emit(partnerInfo);
    this.#partnerAuswahlService.selectPartner(partnerInfo, this.updateRoute());
  }
}
